import { Component, type ErrorInfo, type ReactNode } from "react";

interface Props {
  children: ReactNode;
}

interface State {
  error: Error | null;
}

/** App-wide render-crash guard.
 *
 * React error boundaries have no hook equivalent — `getDerivedStateFromError` /
 * `componentDidCatch` only exist on class components, so this is the one
 * sanctioned class in the app. A render exception in any route lands here with
 * a recoverable fallback instead of unmounting to a blank screen. Inline styles
 * (not Tailwind) so the fallback survives even a CSS/theme failure.
 */
export class ErrorBoundary extends Component<Props, State> {
  override state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    console.error("Render crash caught by ErrorBoundary", error, info);
  }

  private reload = () => {
    window.location.reload();
  };

  override render(): ReactNode {
    const { error } = this.state;
    if (!error) return this.props.children;

    const detail = `${error.message}\n\n${error.stack ?? ""}`;
    return (
      <div
        role="alert"
        style={{
          display: "flex",
          flexDirection: "column",
          gap: 16,
          alignItems: "flex-start",
          maxWidth: 640,
          margin: "0 auto",
          padding: "48px 24px",
          fontFamily: "var(--app-font-family)",
          fontSize: "calc(14px * var(--app-text-scale))",
          lineHeight: 1.6,
          color: "#1f2937",
        }}
      >
        <h1 style={{ fontSize: "calc(20px * var(--app-text-scale))", lineHeight: 1.3, fontWeight: 600, margin: 0 }}>Something went wrong</h1>
        <p style={{ margin: 0, color: "#4b5563" }}>
          The app hit an unexpected error and stopped rendering this screen. Your data is safe — reloading
          usually recovers it.
        </p>
        <button
          type="button"
          onClick={this.reload}
          style={{
            cursor: "pointer",
            border: "none",
            borderRadius: 8,
            padding: "10px 18px",
            background: "#14b8a6",
            color: "#fff",
            fontFamily: "var(--app-font-family)",
            fontSize: "calc(14px * var(--app-text-scale))",
            fontWeight: 600,
          }}
        >
          Reload
        </button>
        <pre
          style={{
            margin: 0,
            width: "100%",
            maxHeight: 280,
            overflow: "auto",
            whiteSpace: "pre-wrap",
            wordBreak: "break-word",
            background: "#f3f4f6",
            borderRadius: 8,
            padding: 12,
            fontFamily: "var(--app-font-family)",
            fontSize: "calc(12px * var(--app-text-scale))",
            lineHeight: 1.5,
            color: "#b91c1c",
          }}
        >
          {detail}
        </pre>
      </div>
    );
  }
}
